/**
 * Your orbit — the profile graphic that places each correspondent around you
 * by how established the relationship is. Pure layout, unit-tested here; the
 * component only renders what this returns.
 *
 * Distance comes from the derived tier (the classifier owns the verdict): in
 * trust sits closest, a new or quiet pair furthest out. Engagement nudges a
 * contact within its tier's band so two "close" friends don't sit on one
 * line. Hidden relationships never reach this function unless the secret code
 * has revealed them — the server leaves them out of `mine` while locked.
 */

import type { RelationshipTier } from '@toiyp/shared';
import type { RelationshipWithPeer } from '../relationships/api';

/** Centre of each tier's band, as a fraction of the orbit's radius. */
export const TIER_RADIUS: Record<RelationshipTier, number> = {
  trusted: 0.5,
  hot: 0.62,
  tepid: 0.73,
  new: 0.84,
  cold: 0.9,
};

/** How far engagement may pull a contact in (or let it drift out) within its band. */
const ENGAGEMENT_SPREAD = 0.04;

/** Beyond this many the orbit gets crowded; the rest are counted, not drawn. */
export const MAX_ORBITERS = 18;

export interface Orbiter {
  id: string;
  name: string;
  tier: RelationshipTier;
  hidden: boolean;
  peer: NonNullable<RelationshipWithPeer['peer']>;
  /** Distance from centre, 0..1 of the orbit radius. */
  radius: number;
  /** Degrees clockwise from twelve o'clock. */
  angle: number;
}

export interface OrbitLayout {
  orbiters: Orbiter[];
  /** Correspondents left off the drawing because the orbit was full. */
  overflow: number;
}

/** Deterministic 0..1 from a string, so a contact keeps its place between visits. */
function unitHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

function closeness(r: RelationshipWithPeer): number {
  return 1 - TIER_RADIUS[r.tier] + (r.engagement ?? 0) * 0.01;
}

export function layoutOrbit(relationships: RelationshipWithPeer[]): OrbitLayout {
  const live = relationships.filter(
    (r): r is RelationshipWithPeer & { peer: NonNullable<RelationshipWithPeer['peer']> } =>
      (r.status === 'accepted' || r.status === 'paused') && r.peer != null,
  );
  // Closest first, so a crowded orbit keeps the people who matter most.
  const shown = [...live].sort((a, b) => closeness(b) - closeness(a)).slice(0, MAX_ORBITERS);

  // Spread contacts evenly around the circle, starting from a stable offset so
  // the arrangement doesn't jump when the list order shifts. Sorting by a hash
  // (not by tier) interleaves near and far, keeping neighbours apart.
  const placed = [...shown].sort((a, b) => unitHash(a.id) - unitHash(b.id));
  const step = placed.length > 0 ? 360 / placed.length : 0;
  const offset = placed.length > 0 ? unitHash(placed.map((r) => r.id).join('|')) * step : 0;

  const orbiters = placed.map((r, i): Orbiter => {
    const engagement = r.engagement ?? 0.5;
    const radius = TIER_RADIUS[r.tier] - (engagement - 0.5) * 2 * ENGAGEMENT_SPREAD;
    return {
      id: r.id,
      name: r.peer.displayName ?? r.peer.handle ?? 'Someone',
      tier: r.tier,
      hidden: r.hidden,
      peer: r.peer,
      radius,
      angle: (offset + i * step) % 360,
    };
  });

  return { orbiters, overflow: live.length - shown.length };
}
